import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { useToast } from '../Toast/Toast';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetSegmented, SetSelect, SetSwitch } from '../SettingsShell/controls';
import { ColourPickerModal } from './ColourPickerModal';
import type { BrainData, PriceQuoting } from '../../settings-brain/types';

export interface BrainViewProps {
  data: BrainData;
  location: string;
  /** Card 10 · See who → /patients?contact=texts-off (§2). */
  onSeeTextsOff?: () => void;
}

const PRICE_OPTIONS: { value: PriceQuoting; label: string }[] = [
  { value: 'refer_to_staff', label: 'Refer to staff' },
  { value: 'range', label: 'Range' },
  { value: 'starting_at', label: 'Starting at' },
];

const ANCHORS: { id: string; label: string }[] = [
  { id: 'who-goes-first', label: 'Who goes first' },
  { id: 'how-often', label: 'How often' },
  { id: 'personality', label: 'Personality' },
  { id: 'money', label: 'Money' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'languages', label: 'Languages' },
  { id: 'calls', label: 'Calls' },
  { id: 'always', label: 'Always' },
  { id: 'checks', label: 'Checks' },
  { id: 'stop', label: 'Stop' },
];

const TWO_PARTY = new Set(['CA', 'FL', 'IL', 'MD', 'MA', 'MT', 'NV', 'NH', 'PA', 'WA']);

/**
 * AI Brain › General — **simplified** (amendment 32 §2, board BR1General). Ten
 * cards, an anchor-chip row, and a green ✓ Saved by every change. No reasoning
 * paragraphs. Card 01, 08, 09 and 10 are read-only; the rest save on change.
 */
export function BrainView({ data, location, onSeeTextsOff }: BrainViewProps) {
  const [d, setD] = useState(data);
  const [pickingColour, setPickingColour] = useState(false);
  const [addLang, setAddLang] = useState(false);
  const [saved, setSaved] = useState<Record<string, number>>({});
  const { toast } = useToast();

  const markSaved = (key: string) => {
    const now = Date.now();
    setSaved((p) => ({ ...p, [key]: now }));
    window.setTimeout(() => { setSaved((p) => (p[key] === now ? (() => { const { [key]: _drop, ...rest } = p; return rest; })() : p)); }, 2000);
  };
  const tick = (key: string) => (saved[key] !== undefined ? <SavedTick /> : null);
  const set = <K extends keyof BrainData>(key: K, value: BrainData[K], savedKey: string) => { setD((p) => ({ ...p, [key]: value })); markSaved(savedKey); };
  const toggle = (group: 'hipaa' | 'recording', id: string, next: boolean) => {
    setD((p) => ({ ...p, [group]: p[group].map((t) => (t.id === id ? { ...t, enabled: next } : t)) }));
    markSaved(`${group}-${id}`);
  };

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[900px] flex-1 overflow-y-auto px-[30px] pb-16 pt-6">
      <SettingsPageHeader
        eyebrow="Settings · AI Brain"
        titleLead="General"
        sub="How ELVA behaves with patients, whichever agent is acting. Set once here; every agent follows it."
      />

      {/* anchor chips — sticky */}
      <div className="sticky top-0 z-10 -mx-[30px] mb-5 flex flex-wrap gap-1.5 border-b border-[var(--border-default)] bg-[var(--surface-page,var(--surface-card))] px-[30px] py-2.5">
        {ANCHORS.map((a) => (
          <a key={a.id} href={`#${a.id}`} className="rounded-full border border-[var(--border-default)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]">{a.label}</a>
        ))}
      </div>

      <SettingsCards>
        {/* 01 Who goes first */}
        <SettingsCard n="01" id="who-goes-first" title="Who goes first" sub="When two agents want the same patient at the same time, the one higher on this list goes first. Set by ELVA." headRight={<LockChip />}>
          <div className="px-6 py-3">
            {d.purposes.map((p) => (
              <div key={p.rank} className="grid grid-cols-[28px_1fr_auto] items-baseline gap-3 border-t border-[color:#EDEFF5] py-2 first:border-0 text-[13px]">
                <span className="font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-muted)]">{p.rank}</span>
                <span className="font-semibold text-[var(--text-primary)]">{p.label}</span>
                <span className="text-[11.5px] text-[var(--text-muted)]">{p.raisedBy}</span>
              </div>
            ))}
          </div>
          <SettingsNote tone="plain">A patient’s own message is never on this list — ELVA answers it straight away, at any hour. Recall campaigns follow the order on the Recall page.</SettingsNote>
        </SettingsCard>

        {/* 02 How often */}
        <SettingsCard n="02" id="how-often" title="How often ELVA reaches out" sub="One limit for every agent together, per patient.">
          <SettingsRow
            label="Conversations ELVA starts with a patient"
            desc="Texts, emails and AI calls from every agent, added up. 1 to 5."
            control={<span className="flex items-center gap-2"><span className="text-[12.5px] text-[var(--text-muted)]">At most</span><SetSelect value={String(d.contactLimit)} options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))} onChange={(v) => { set('contactLimit', Number(v), 'limit'); }} ariaLabel="Conversations at most" /><span className="text-[12.5px] text-[var(--text-muted)]">in 7 days</span>{tick('limit')}</span>}
          />
          <SettingsRow label="Never more than one a day" desc="Two agents with something to say the same day: the one higher on the list goes, the other waits." control={<LockChip />} />
          <SettingsRow
            label="Combine two messages into one"
            desc="When two are due the same day, ELVA may send one that covers both — “your cleaning is due, and there’s $45 left from your last visit”. Post-op checks, birthdays and same-day openings always go alone."
            last
            control={<span className="flex items-center gap-2"><SetSwitch checked={d.combineMessages} onChange={(v) => { set('combineMessages', v, 'combine'); }} label="Combine two messages" />{tick('combine')}</span>}
          />
          <SettingsNote tone="plain">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">Never counted</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">{d.neverCounted.map((c) => <span key={c} className="rounded-full border border-[var(--border-default)] bg-[var(--surface-card)] px-2 py-0.5 text-[11px] text-[var(--text-secondary)]">{c}</span>)}</div>
          </SettingsNote>
        </SettingsCard>

        {/* 03 Personality */}
        <SettingsCard n="03" id="personality" title="Personality and voice" sub="The same ELVA on every channel, for every agent.">
          <SettingsRow label="Name" desc="Used in greetings, voicemails and introductions." control={<span className="font-[var(--font-mono)] text-[13px] text-[var(--text-primary)]">{d.name}</span>} />
          <SettingsRow label="Voice" desc="Press Play to hear it say your greeting." control={<span className="flex items-center gap-2"><SetSelect value={d.voice} optionGroups={d.voiceGroups.map((g) => ({ label: g.group, options: g.voices.map((v) => ({ value: v.name, label: `${v.name} — ${v.description}` })) }))} onChange={(v) => { set('voice', v, 'voice'); }} ariaLabel="Voice" /><button type="button" className="inline-flex items-center gap-1 rounded-[8px] border border-[var(--border-default)] px-2.5 py-1 text-[12px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]" onClick={() => { toast({ title: `Playing ${d.voice}…`, variant: 'success' }); }}><Icon kind="play" size={12} stroke={2} /> Play</button></span>} />
          <SettingsRow label="Warmth" control={<Slider value={d.warmth} left="Professional" right="Warm" />} />
          <SettingsRow label="Formality" control={<Slider value={d.formality} left="Casual" right="Formal" />} />
          <SettingsRow label="Brand colour" desc="Themes the pages patients open: their plan, payment and booking pages." last control={<span className="flex items-center gap-2"><span className="inline-block h-5 w-5 rounded-full border border-[var(--border-default)]" style={{ background: d.brandColour }} /><button type="button" className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { setPickingColour(true); }}>Change</button></span>} />
        </SettingsCard>

        {/* 04 Money */}
        <SettingsCard n="04" id="money" title="Money" sub="What ELVA may say about fees and balances, on every channel.">
          <SettingsRow label="Fees" desc="How ELVA answers “how much is it?”" control={<span className="flex items-center gap-2"><SetSegmented value={d.priceQuoting} options={PRICE_OPTIONS} onChange={(v) => { set('priceQuoting', v, 'fees'); }} ariaLabel="Fees" />{tick('fees')}</span>} />
          <SettingsRow
            label="Say a balance amount"
            desc="Only after ELVA has checked who it’s talking to. Off: “there’s a balance — the front desk can go through it with you.” Accounts receivable needs this on to call about balances."
            last
            control={<span className="flex items-center gap-2"><SetSwitch checked={d.sayBalance} onChange={(v) => { set('sayBalance', v, 'saybal'); }} label="Say a balance amount" />{tick('saybal')}</span>}
          />
        </SettingsCard>

        {/* 05 Privacy */}
        <SettingsCard n="05" id="privacy" title="Patient privacy" sub="How ELVA handles health information on calls, texts and emails.">
          {d.hipaa.map((t, i) => (
            <SettingsRow key={t.id} label={t.label} desc={t.desc} last={i === d.hipaa.length - 1} control={<span className="flex items-center gap-2"><SetSwitch checked={t.enabled} onChange={(v) => { toggle('hipaa', t.id, v); }} label={t.label} />{tick(`hipaa-${t.id}`)}</span>} />
          ))}
        </SettingsCard>

        {/* 06 Languages */}
        <SettingsCard n="06" id="languages" title="Languages" sub="Each patient gets messages in their own language. You write in English." headRight={<button type="button" className="rounded-[8px] border border-[var(--border-default)] px-2.5 py-1 text-[12px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]" onClick={() => { setAddLang(true); }}>Add a language</button>}>
          {d.languages.map((l) => (
            <SettingsRow key={l.id} label={l.label} desc={`${l.channels} · ${l.patients.toLocaleString()} patients`} control={l.locked ? <LockChip /> : <SetSwitch checked onChange={() => { markSaved(`lang-${l.id}`); }} label={l.label} />} />
          ))}
          {/* spec 44 §3: AI calls work only in languages ELVA has tested; texts and
              emails work in any. One line under the list says what happens otherwise. */}
          <div className="px-6 pb-[18px] pt-3 text-[12.5px] leading-[1.6] text-[var(--text-muted)]">
            On a call in a language ELVA hasn’t tested, ELVA carries on in English and offers a person.
          </div>
        </SettingsCard>

        {/* 07 Calls */}
        <SettingsCard n="07" id="calls" title="Calls" sub="Recording and saying it’s an AI.">
          {d.recording.map((t, i) => {
            const twoParty = TWO_PARTY.has(d.locationState);
            const desc = t.id === 'r2'
              ? (twoParty
                ? `Required in two-party consent states — this location is in ${d.locationState}, so keep it on.`
                : `Required in two-party consent states — this location is in ${d.locationState}, which isn’t one.`)
              : t.id === 'r1' ? 'Encrypted. Kept as long as the call type needs.'
                : '“I’m Elva, the AI receptionist at Alamo Heights Dental.”';
            const label = t.id === 'r1' ? 'Record calls' : t.id === 'r2' ? 'Announce the recording and wait for a yes' : 'Say it’s an AI at the start';
            return <SettingsRow key={t.id} label={label} desc={desc} last={i === d.recording.length - 1} control={<span className="flex items-center gap-2"><SetSwitch checked={t.enabled} onChange={(v) => { toggle('recording', t.id, v); }} label={label} />{tick(`recording-${t.id}`)}</span>} />;
          })}
        </SettingsCard>

        {/* 08 Always */}
        <SettingsCard n="08" id="always" title="Always, on every channel" sub="Locked for every practice. Each agent’s page lists its own extra limits." headRight={<LockChip />}>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 px-6 py-4 max-[720px]:grid-cols-1">
            {d.hardLimits.map((h) => (
              <div key={h.id} className="flex items-baseline gap-2 text-[12.5px] text-[var(--text-primary)]"><Icon kind="lock" size={12} stroke={2} style={{ color: 'var(--text-muted)' }} />{h.label}</div>
            ))}
          </div>
        </SettingsCard>

        {/* 09 Checks */}
        <SettingsCard n="09" id="checks" title="Every message is checked before it goes" sub="Nothing to set." headRight={<LockChip />}>
          <SettingsNote tone="plain">Every number, date and name must match your records, and nothing on the lists above may appear. If a written message fails, the step’s backup text goes instead; if that fails too, nothing is sent and it’s in the Communication log. If checks start failing across agents, ELVA switches everyone to backup texts on its own and shows a banner until it’s fixed.</SettingsNote>
        </SettingsCard>

        {/* 10 Stop */}
        <SettingsCard n="10" id="stop" title="When a patient says stop" sub="How ELVA hears it, on every channel." headRight={<LockChip />}>
          <div className="grid grid-cols-3 gap-4 px-6 py-4 max-[720px]:grid-cols-1">
            <StopCol title="Replies STOP" body="Texts from this number stop for that patient, from every agent, until they text START." />
            <StopCol title="Says it in a sentence" body="“Stop sending me aligner stuff” stops that topic only. “Stop texting me” stops texts. “I’ll stop by Tuesday” isn’t an opt-out." />
            <StopCol title="Not sure what they meant" body="ELVA asks once to be sure, then honours it." />
          </div>
          <SettingsNote tone="plain">
            <span className="flex flex-wrap items-center gap-2">
              <span><span className="font-semibold text-[var(--text-secondary)]">{d.stopStats.textsOff}</span> patients have texts off · <span className="font-semibold text-[var(--text-secondary)]">{d.stopStats.stoppedTopic}</span> stopped one topic · Their reminders go by email or call instead</span>
              <button type="button" className="ml-auto font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onSeeTextsOff}>See who →</button>
            </span>
          </SettingsNote>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter location={location} />

      <ColourPickerModal open={pickingColour} value={d.brandColour} onClose={() => { setPickingColour(false); }} onSave={(c) => { set('brandColour', c, 'colour'); setPickingColour(false); }} />


      {addLang ? (
        <LanguagePickerModal
          options={d.languagesAvailable}
          onClose={() => { setAddLang(false); }}
          onAdd={(lang) => {
            setD((p) => ({ ...p, languages: [...p.languages, { id: lang.toLowerCase(), label: lang, channels: 'Texts and emails', patients: 0 }], languagesAvailable: p.languagesAvailable.filter((x) => x !== lang) }));
            setAddLang(false);
            toast({ title: `${lang} added`, variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

function SavedTick() {
  return <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1b5f45]"><Icon kind="check" size={12} stroke={2.4} /> Saved</span>;
}
function LockChip() {
  return <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--text-muted)]"><Icon kind="lock" size={11} stroke={2} /> Locked</span>;
}
function Slider({ value, left, right }: { value: number; left: string; right: string }) {
  return (
    <span className="flex w-[220px] items-center gap-2 text-[11px] text-[var(--text-muted)]">
      {left}
      <span className="relative h-1.5 flex-1 rounded-full bg-[var(--surface-sunken)]"><span className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-[var(--brand-indigo)]" style={{ left: `calc(${String(value)}% - 6px)` }} /></span>
      {right}
    </span>
  );
}
function StopCol({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
      <div className="text-[12px] font-bold text-[var(--text-primary)]">{title}</div>
      <div className="mt-1 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]">{body}</div>
    </div>
  );
}
function LanguagePickerModal({ options, onClose, onAdd }: { options: string[]; onClose: () => void; onAdd: (lang: string) => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.4)] px-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="w-full max-w-[360px] rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-modal)]" onClick={(e) => { e.stopPropagation(); }}>
        <h2 className="m-0 text-[15px] font-bold text-[var(--text-primary)]">Add a language</h2>
        <div className="mt-3 flex flex-col gap-1">
          {options.map((o) => (
            <button key={o} type="button" className="rounded-[8px] px-3 py-2 text-left text-[13px] text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]" onClick={() => { onAdd(o); }}>{o}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
